import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { cn } from '../cn'

interface ModalFieldHeaderInputProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  placeholder: string
  type: 'title' | 'description'
}

export function ModalFieldHeaderInput<T extends FieldValues>({
  control,
  name,
  placeholder,
  type,
}: ModalFieldHeaderInputProps<T>) {
  const {
    field: { onChange, value, onBlur, ref },
    fieldState: { error },
  } = useController({ name, control })

  const baseInputClasses = cn(
    'w-full max-w-[500px] bg-transparent border-t-0 border-x-0 border-b',
    'outline-none focus:outline-none active:outline-none',
    'ring-0 focus:ring-0 active:ring-0',
    'transition-colors duration-200',
    'placeholder:text-gray-500',
    // Conditional styling based on type
    type === 'title' &&
      'text-xl font-semibold leading-[1.4] text-gray-700 py-1 px-0',
    type === 'description' &&
      'text-sm font-light leading-8 text-gray-700 py-0.5 px-0',
    // Border states
    error ? 'border-modal-field-error' : 'border-transparent',
    // Hover state (only when no error)
    !error && 'hover:border-modal-field-description-focus',
    // Focus states (only when no error)
    !error &&
      type === 'title' &&
      'focus:border-brand-active focus:shadow-field-focus',
    !error &&
      type === 'description' &&
      'focus:border-modal-field-description-focus focus:shadow-field-focus'
  )

  return (
    <div className="flex flex-col w-full">
      <input
        ref={ref}
        type="text"
        value={value ?? ''}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className={baseInputClasses}
      />
      {error && (
        <span className="text-xxs leading-[1.2] text-modal-field-error mt-1">
          {error.message}
        </span>
      )}
    </div>
  )
}
